import { supabase } from '../lib/supabase'

/**
 * Servicio centralizado de uploads.
 *
 * Responsabilidades:
 * - Subir archivos a Supabase Storage
 * - Generar URLs públicas
 * - Retornar metadata normalizada
 *
 * Flujo general:
 * 1. Sanitizar nombre del archivo
 * 2. Subir archivo al bucket
 * 3. Obtener URL pública
 * 4. Retornar metadata estructurada
 *
 * @namespace uploadService
 */
export const uploadService = {
  /**
   * Bucket principal utilizado para almacenar archivos.
   *
   * @type {string}
   */
  bucket: 'tickets',

  /**
   * Sube un archivo al bucket configurado y retorna
   * toda la metadata necesaria para persistencia.
   *
   * Pasos internos:
   * 1. Genera nombre seguro del archivo
   * 2. Construye path único en storage
   * 3. Ejecuta upload en Supabase Storage
   * 4. Obtiene URL pública
   * 5. Retorna metadata del upload
   *
   * @async
   *
   * @param {File} file
   * Archivo seleccionado por el usuario.
   *
   * @param {string|number} ticketId
   * ID del ticket asociado al archivo.
   *
   * @param {string|number} userId
   * ID del usuario que realiza la subida.
   *
   * @returns {Promise<{
   *   path: string,
   *   url: string,
   *   fileName: string,
   *   size: number,
   *   mimeType: string,
   *   bucket: string,
   *   uploadedBy: string|number,
   *   ticketId: string|number
   * }>}
   * Metadata completa del archivo subido.
   *
   * @throws {Error}
   * Puede lanzar errores relacionados con:
   * - Upload a Supabase Storage
   * - Generación de URL pública
   * - Problemas de red
   *
   * @example
   * const result = await uploadService.uploadFile(
   *   file,
   *   123,
   *   authUser.id
   * )
   *
   * console.log(result.url)
   */
  async uploadFile(file, ticketId, userId) {
    /**
     * Sanitiza el nombre del archivo:
     * - reemplaza espacios por "-"
     * - elimina caracteres especiales
     */
    const safeFileName = file.name
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9._-]/g, '')

    /**
     * Path final dentro del bucket.
     *
     * Formato:
     * ticketId/timestamp-fileName
     */
    const storagePath = `${ticketId}/${Date.now()}-${safeFileName}`

    /**
     * 1. Upload físico al bucket
     */
    const { error: storageError } = await supabase.storage
      .from(this.bucket)
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false,
      })

    /**
     * Manejo de error de storage.
     */
    if (storageError) {
      console.warn(
        `[uploadService] ⚠️ Falló la subida al bucket: ${file.name} — ${storageError.message}`
      )

      throw new Error(`Storage: ${storageError.message}`)
    }

    /**
     * 2. Obtener URL pública del archivo
     */
    const { data } = supabase.storage
      .from(this.bucket)
      .getPublicUrl(storagePath)

    /**
     * URL pública final.
     *
     * @type {string}
     */
    const publicUrl = data.publicUrl

    /**
     * 3. Retornar metadata estructurada
     */
    return {
      path: storagePath,
      url: publicUrl,
      fileName: file.name,
      size: file.size,
      mimeType: file.type,
      bucket: this.bucket,
      uploadedBy: userId,
      ticketId,
    }
  },
}
